// Эцэг эхийн нүүр v0.1 — /guardian/dashboard-ийн хэсгүүд: мэдэгдэл, хийх зүйлс, удахгүй болох, сургалт
// Хэв маяг: Tomujin Design System (Parent home v0.1 загвар)
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiVolume2, FiX, FiCreditCard, FiCalendar, FiVideo, FiCheck, FiClock, FiChevronRight, FiPlay, FiFileText, FiArrowRight } from "react-icons/fi";
import dayjs from "dayjs";
import { DOW_FULL } from "./AttendanceDays";
import { dueText } from "utils/due";
import { ProgressBar, itemPath, seriesKind } from "pages/Learning/common";

const DOW_SHORT = ["Ня", "Да", "Мя", "Лх", "Пү", "Ба", "Бя"];

// Мэдэгдэл/хийх зүйлийн холбоос: series → /learning/:id, "/..." → портал доторх, бусад → шинэ цонх
function DashLink({ link, className, children, ...rest }) {
  if (!link) return null;
  if (link.kind === "series") return <Link to={link.itemId ? itemPath(link.itemId, link.seriesId) : `/learning/${link.seriesId}`} className={className} {...rest}>{children || link.label}</Link>;
  if (link.kind === "consent") return <Link to={`/consent/${link.formId}`} className={className} {...rest}>{children || link.label}</Link>;
  if (link.kind === "todo") return <Link to={`/todo/${link.todoId}`} className={className} {...rest}>{children || link.label}</Link>;
  if (String(link.url || "").startsWith("/")) return <Link to={link.url} className={className} {...rest}>{children || link.label}</Link>;
  return <a href={link.url} target="_blank" rel="noopener noreferrer" className={className} {...rest}>{children || link.label}</a>;
}

// Хэсгийн гарчиг: (eyebrow) + гарчиг + тайлбар, баруун талд холбоос/шошго
export function SectionHead({ eyebrow, title, sub, right }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-1.5 md:mb-2">
      <div className="min-w-0">
        {eyebrow && <span className="block text-[11px] md:text-xs font-bold tracking-[0.12em] uppercase text-[#0e7490]">{eyebrow}</span>}
        <h2 className="m-0 text-xl md:text-[22px] font-bold leading-snug text-[#1c1c1c]">{title}</h2>
        {sub && <div className="text-sm md:text-[15px] text-[#6e5e8a]">{sub}</div>}
      </div>
      {right}
    </div>
  );
}

const MoreLink = ({ to, children }) => (
  <Link to={to} className="shrink-0 inline-flex items-center gap-0.5 min-h-[44px] md:min-h-0 md:py-2 text-sm md:text-[15px] font-semibold text-primary hover:text-[#3d1b6e]">
    {children}<FiChevronRight size={16} />
  </Link>
);

// ── Мэдэгдэл (шар баннер) ──
export function Announcements({ list, onDismiss }) {
  return list.map((a) => (
    <section key={a.id} aria-label="Зарлал"
      className="bg-[#fff8db] border border-[#f2cd30] rounded-[18px] md:rounded-[20px] p-4 md:px-6 md:py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
      <div className="flex items-start md:items-center gap-3 md:gap-5 flex-1 min-w-0">
        <span className="h-10 w-10 md:h-12 md:w-12 rounded-xl md:rounded-[14px] bg-[#f2cd30] text-[#1c1c1c] flex items-center justify-center shrink-0">
          <FiVolume2 size={20} />
        </span>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-[11px] md:text-xs font-bold tracking-[0.12em] uppercase text-[#7a5a00]">{a.level === "important" ? "Чухал зарлал" : "Зарлал"}</span>
          <span className="text-base md:text-lg font-bold text-[#1c1c1c]">{a.title}</span>
          {a.body && <span className="text-sm md:text-[15px] text-[#5b4e70] whitespace-pre-line">{a.body}</span>}
        </div>
        <button onClick={() => onDismiss(a.id)} aria-label="Зарлалыг хаах"
          className="md:hidden h-11 w-11 -mt-2.5 -mr-2.5 rounded-full flex items-center justify-center shrink-0 text-[#5b4e70] hover:bg-black/5">
          <FiX size={18} />
        </button>
      </div>
      {/* Мобайлд хаах товч дээд буланд — холбоосгүй бол энэ мөр зөвхөн desktop-д */}
      <div className={`${a.link ? "flex" : "hidden md:flex"} items-center gap-2`}>
        {a.link && (
          <DashLink link={a.link}
            className="flex-1 md:flex-none h-11 px-5 rounded-xl bg-primary text-white hover:text-white hover:bg-[#4b0d76] text-[15px] font-bold inline-flex items-center justify-center">
            {a.link.label || "Дэлгэрэнгүй"}
          </DashLink>
        )}
        <button onClick={() => onDismiss(a.id)} aria-label="Зарлалыг хаах"
          className="hidden md:flex h-11 w-11 rounded-full items-center justify-center shrink-0 text-[#5b4e70] hover:bg-black/5">
          <FiX size={20} />
        </button>
      </div>
    </section>
  ));
}

// ── Хийх зүйлс ── эцэг эх гараар чагтлахгүй: сургалт үзэж дуусахад, уулзалт захиалахад, төлбөр төлөхөд өөрөө "хийсэн" болно
function TodoIcon({ t }) {
  const box = "h-6 w-6 rounded-lg flex items-center justify-center shrink-0";
  if (t.done) return <span className={`${box} rounded-full bg-[#22c55e] text-white`}><FiCheck size={14} strokeWidth={3} /></span>;
  if (t.source === "tuition") return <span className={`${box} bg-[#fff6d6] text-[#7a5a00]`}><FiCreditCard size={14} /></span>;
  if (t.link?.doc) return <span className={`${box} bg-[#f3eeff] text-primary`}><FiFileText size={14} /></span>;
  if (t.progress || t.link?.kind === "series") return <span className={`${box} bg-[#f3eeff] text-primary`}><FiVideo size={14} /></span>;
  return <span className={`${box} rounded-full border-2 border-[#d9cdea]`} />;
}

const ACTION_TAG = { letter: "Захидал", consent: "Зөвшөөрөл", read: "Унших" };
const todoTag = (t) =>
  ACTION_TAG[t.action] || (t.link?.doc ? "Унших" : t.source === "tuition" ? "Төлбөр" : t.source === "learning" || t.link?.kind === "series" ? "Сургалт" : "Сургууль");

function TodoRow({ t }) {
  // Хийсний дараа ч захидал/зөвшөөрлийг нээж харах, засах боломжтой
  const open = !!t.link && (!t.done || !!t.action);
  const due = t.dueDate ? dayjs(t.dueDate).format("MM/DD") : null;
  const body = (
    <>
      <span className="pt-0.5"><TodoIcon t={t} /></span>
      <span className="flex-1 min-w-0 flex flex-col gap-1.5 md:gap-0.5">
        <span className={`text-[15px] md:text-base font-semibold ${t.done ? "text-[#6e5e8a] line-through decoration-[#d9cdea]" : "text-[#1c1c1c]"}`}>{t.title}</span>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] md:text-sm text-[#6e5e8a]">
          <span className="md:hidden inline-flex items-center h-6 px-2.5 rounded-full bg-[#f3eeff] border border-[#e5d8f5] text-primary text-xs font-semibold">{todoTag(t)}</span>
          {t.progress && <span className={t.done ? "text-[#15803d] font-semibold" : "font-semibold text-primary"}>{t.progress.done} / {t.progress.total}{t.done ? " · дууссан" : ""}</span>}
          {!t.done && due && (
            <span className={`inline-flex items-center gap-1 ${t.overdue ? "text-[#dc2626] font-semibold" : ""}`}>
              <FiClock size={13} />{t.overdue ? `${due} хугацаа хэтэрсэн` : dueText(t.dueDate)}
            </span>
          )}
          {t.source === "tuition" && t.amount ? <span className="font-semibold text-[#1c1c1c]">₮{Number(t.amount).toLocaleString()}</span> : null}
          {t.child && <span>{t.child}</span>}
        </span>
      </span>
      <span className="hidden md:inline-flex items-center h-7 px-3 rounded-full bg-[#f3eeff] border border-[#e5d8f5] text-primary text-[13px] font-semibold whitespace-nowrap">{todoTag(t)}</span>
      {open ? (
        <span className="h-11 w-11 md:h-10 md:w-10 -my-2 -mr-2.5 md:m-0 rounded-full flex items-center justify-center text-primary shrink-0"><FiChevronRight size={20} /></span>
      ) : <span className="w-8 md:w-10 shrink-0" />}
    </>
  );
  const cls = "flex items-start md:items-center gap-3 md:gap-3.5 py-3.5 md:px-1 border-t border-[#efedf4] text-inherit hover:text-inherit";
  return open
    ? <DashLink link={t.link} className={`${cls} hover:bg-[#faf7ff] -mx-2 px-2 md:mx-0 rounded-lg`} aria-label={`${t.title} — нээх`}>{body}</DashLink>
    : <div className={cls}>{body}</div>;
}

export function TodoCard({ todos }) {
  const left = todos.filter((t) => !t.done).length;
  return (
    <section aria-labelledby="todo-h" className="tj-card px-4 pt-[18px] pb-1.5 md:px-6 md:pt-6 md:pb-3">
      <SectionHead title={<span id="todo-h">Таны хийх зүйлс</span>} sub="Таны оролцоо шаардлагатай"
        right={<span className="inline-flex items-center h-7 md:h-[30px] px-3 md:px-3.5 rounded-full bg-[#fff6d6] border border-[#f2d77a] text-[#7a5a00] text-[13px] md:text-sm font-semibold whitespace-nowrap">{left} үлдсэн</span>} />
      <div>{todos.map((t) => <TodoRow key={t.key} t={t} />)}</div>
    </section>
  );
}

// ── Удахгүй болох уулзалтууд, зөвлөхтэй уулзалт захиалах ──
const hasTime = (d) => d.format("HH:mm") !== "00:00";
const slotText = (iso) => {
  const d = dayjs(iso);
  const near = d.diff(dayjs().startOf("day"), "day") < 7;
  return `${near ? "" : d.format("MM/DD ")}${DOW_FULL[d.day()]} ${d.format("HH:mm")}`;
};

// Нэг уулзалт/арга хэмжээний карт (нягт): огнооны хайрцаг | гарчиг, цаг, тайлбар | онлайн/биечлэн + холбоос
function EventCard({ d, end, title, desc, when, place, format, link }) {
  const meta = [when, place, end].filter(Boolean).join(" · ");
  return (
    <article className="bg-[#faf7ff] border border-[#ede0ff] rounded-2xl p-3 md:p-3.5 flex items-center gap-3">
      <div className="h-[52px] w-[48px] md:h-[56px] md:w-[52px] rounded-xl bg-white flex flex-col items-center justify-center shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-[0.04em] text-primary leading-none">{d.month() + 1}-р сар</span>
        <span className="text-[22px] md:text-2xl font-extrabold leading-none text-[#1c1c1c] mt-1">{d.format("D")}</span>
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <h3 className="m-0 text-[15px] md:text-base font-bold leading-snug text-[#1c1c1c]">{title}</h3>
        <span className="text-[13px] text-[#5b4e70]">{meta}</span>
        {desc && <span className="text-[13px] text-[#6e5e8a] line-clamp-2">{desc}</span>}
        {(format || link) && (
          <div className="flex items-center gap-2 mt-1">
            {format && <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-white border border-[#e5d8f5] text-[12px] font-semibold text-[#1c1c1c]">{format}</span>}
            {link && (
              <a href={link} target="_blank" rel="noopener noreferrer"
                className="ml-auto h-8 px-3.5 rounded-full bg-primary text-white hover:text-white hover:bg-[#4b0d76] text-[13px] font-bold inline-flex items-center">Холбоос нээх</a>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

// Захиалсан уулзалт + эцэг эхэд харуулах арга хэмжээ, огноогоор
export function Upcoming({ upcoming }) {
  const { events = [], meetings = [] } = upcoming || {};
  const rows = [
    ...meetings.map((m) => {
      const d = dayjs(m.startTime);
      return {
        key: `m${m.id}`, d,
        title: m.note || (m.advisor ? `${m.advisor}-тай уулзалт` : "Зөвлөхтэй уулзалт"),
        desc: null,
        when: `${DOW_FULL[d.day()]}, ${d.format("M/D")} · ${d.format("HH:mm")}${m.durationMin ? ` · ${m.durationMin} мин` : ""}`,
        format: m.type === "online" ? "Онлайн" : "Биечлэн", link: m.meetingLink,
      };
    }),
    ...events.map((e) => {
      const d = dayjs(e.startDate);
      const end = e.endDate ? dayjs(e.endDate) : null;
      return {
        key: `e${e.id}`, d, title: e.name, desc: e.note,
        when: `${DOW_FULL[d.day()]}, ${d.format("M/D")}${hasTime(d) ? ` · ${d.format("HH:mm")}` : ""}`,
        end: end && !end.isSame(d, "day") ? `${end.format("M/D")} хүртэл` : null,
      };
    }),
  ].sort((a, b) => a.d.valueOf() - b.d.valueOf());

  return (
    <section aria-labelledby="ev-h" className="tj-card px-4 pt-[18px] pb-4 md:p-6">
      <SectionHead title={<span id="ev-h">Удахгүй болох уулзалтууд</span>} />
      {rows.length ? (
        <div className="grid gap-2.5 md:gap-3 mt-2 md:grid-cols-2">
          {rows.map(({ key, ...r }) => <EventCard key={key} {...r} />)}
        </div>
      ) : (
        <div className="border border-dashed border-[#d9cdea] rounded-[14px] md:rounded-2xl px-4 py-5 mt-1.5 flex flex-col items-center text-center gap-1.5">
          <span className="h-11 w-11 rounded-full bg-[#eee7f3] text-primary flex items-center justify-center"><FiCalendar size={20} /></span>
          <span className="text-base font-bold text-[#1c1c1c]">Ойрын үед товлосон уулзалт алга</span>
          <span className="text-sm text-[#6e5e8a]">Шинэ уулзалт, арга хэмжээ товлогдоход энд харагдана.</span>
        </div>
      )}
    </section>
  );
}

// Зөвлөхтэй уулзалт захиалах — сургууль "уулзалт захиалах" хийх зүйл тавьсан, хараахан захиалаагүй үед.
// Захиалсны дараа уулзалт нь "Удахгүй болох уулзалтууд"-д гарна.
export function MeetingSection({ upcoming, booking, className = "" }) {
  const navigate = useNavigate();
  const { meetings = [], advisors = [] } = upcoming || {};
  if (!booking || booking.done || meetings.some((m) => !m.group)) return null;
  const book = () => navigate("/college-prep/children?book=1");

  return (
    <section aria-labelledby="mt-h" className={`tj-card px-4 pt-[18px] pb-4 md:px-6 md:pt-6 md:pb-5 ${className}`}>
      <SectionHead eyebrow="Их сургуульд элсэлт" title={<span id="mt-h">Зөвлөхтэй уулзалт</span>}
        sub={booking.dueDate ? `${dueText(booking.dueDate)} цагаа сонгоно уу` : "Хүүхдийнхээ талаар зөвлөхтэй ярилцаарай"} />
      <div className="flex flex-col gap-2 mt-2">
        {advisors.map((a) => (
          <button key={a.id} onClick={book} className="flex items-center gap-2.5 bg-white border border-[#e9e8f0] rounded-xl px-3 py-2.5 text-left hover:bg-[#faf7ff]">
            <span className="h-[34px] w-[34px] rounded-full bg-[#f3eeff] text-primary text-[13px] font-bold flex items-center justify-center shrink-0">{(a.name || "?").charAt(0)}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold truncate text-[#1c1c1c]">{a.name}</span>
              {a.next && <span className="block text-xs text-[#6e5e8a]">Ойрын сул цаг: {slotText(a.next)}</span>}
            </span>
            <span className="text-xs font-bold text-primary shrink-0">{a.openSlots} цаг</span>
          </button>
        ))}
        {!advisors.length && <span className="text-sm text-[#6e5e8a] py-1">Одоогоор сул цаг алга — удахгүй нэмэгдэнэ.</span>}
        <button onClick={book} className="mt-1 h-11 rounded-full bg-primary text-white text-[15px] font-bold hover:bg-[#4b0d76]">Уулзалт товлох</button>
      </div>
    </section>
  );
}

// ── Сургалт ── desktop: том зурагтай карт (grid), мобайл: жижиг дүрстэй мөр
const TILE_BG = ["linear-gradient(120deg, #56198c 0%, #7c32c3 100%)", "#0e7490", "#3d1b6e"];

function tileInfo(s, cont) {
  const isVideo = seriesKind(s) === "video";
  const complete = s.itemCount > 0 && s.done >= s.itemCount;
  const resume = cont && cont.seriesId === s.id;
  const to = resume ? itemPath(cont.itemId, s.id) : `/learning/${s.id}`;
  const cta = complete ? (isVideo ? "Дахин үзэх" : "Дахин унших") : resume || s.started > 0 ? "Үргэлжлүүлэх" : isVideo ? "Үзэх" : "Унших";
  const kind = isVideo ? "Цуврал бичлэг" : "Гарын авлага";
  const meta = complete
    ? (isVideo ? "Бүтэн үзсэн" : "Уншсан")
    : isVideo ? `${s.itemCount} бичлэг${s.durationSec ? ` · ${Math.max(1, Math.round(s.durationSec / 60))} мин` : ""}` : null;
  return { isVideo, complete, to, cta, kind, meta };
}

function SeriesTile({ s, i, cont }) {
  const { isVideo, complete, to, cta, kind, meta } = tileInfo(s, cont);
  const Icon = complete ? FiCheck : isVideo ? FiPlay : FiFileText;
  return (
    <Link to={to} className="flex flex-col border border-[#e9e8f0] rounded-2xl overflow-hidden bg-white text-[#1c1c1c] hover:text-[#1c1c1c] hover:shadow-[0_1px_3px_rgba(0,0,0,.1),0_1px_2px_-1px_rgba(0,0,0,.1)] transition-shadow">
      <div className="h-[140px] relative flex items-center justify-center" style={{ background: TILE_BG[i % TILE_BG.length] }}>
        <span className="absolute top-3 left-3 inline-flex items-center h-[26px] px-2.5 rounded-full bg-white text-primary text-xs font-bold">{kind}</span>
        <span className="h-[52px] w-[52px] rounded-full bg-white text-primary flex items-center justify-center"><Icon size={22} className={isVideo && !complete ? "fill-primary ml-0.5" : ""} /></span>
        {s.started > 0 && !complete && (
          <span className="absolute inset-x-0 bottom-0 h-[5px] bg-white/25"><span className="block h-full bg-[#f2cd30]" style={{ width: `${Math.round((s.done * 100) / (s.itemCount || 1))}%` }} /></span>
        )}
      </div>
      <div className="px-[18px] pt-4 pb-[18px] flex flex-col gap-2.5 flex-1">
        <span className="text-base font-bold leading-snug">{s.title}</span>
        {meta && <span className={`text-[13px] ${complete ? "text-[#15803d] font-semibold" : "text-[#6e5e8a]"}`}>{meta}</span>}
        <span className="mt-auto text-[15px] font-semibold text-primary inline-flex items-center gap-1">{cta}<FiArrowRight size={16} /></span>
      </div>
    </Link>
  );
}

function SeriesRow({ s, i, cont }) {
  const { isVideo, complete, to, kind, meta } = tileInfo(s, cont);
  const Icon = complete ? FiCheck : isVideo ? FiPlay : FiFileText;
  return (
    <Link to={to} className="flex items-center gap-3 py-3 border-t border-[#efedf4] text-[#1c1c1c] hover:text-[#1c1c1c]">
      <span className="h-[60px] w-[60px] rounded-[14px] shrink-0 flex items-center justify-center text-white" style={{ background: complete ? "#22c55e" : TILE_BG[i % TILE_BG.length] }}>
        <Icon size={22} className={isVideo && !complete ? "fill-white ml-0.5" : ""} />
      </span>
      <span className="flex-1 min-w-0 flex flex-col gap-1">
        <span className="text-[15px] font-bold leading-snug">{s.title}</span>
        <span className={`text-xs font-semibold ${complete ? "text-[#15803d]" : "text-primary"}`}>{kind}{meta ? ` · ${meta}` : ""}</span>
        {s.started > 0 && !complete && (
          <span className="flex items-center gap-2"><ProgressBar value={s.done} total={s.itemCount} className="h-1" /><span className="text-xs font-semibold text-[#6e5e8a]">{s.done}/{s.itemCount}</span></span>
        )}
      </span>
      <FiChevronRight size={20} className="text-primary shrink-0" />
    </Link>
  );
}

export function LearningSection({ learning }) {
  const series = learning?.series || [];
  if (!series.length) return null;
  const cats = [...new Set(series.map((s) => s.category).filter(Boolean))];
  const cont = learning?.continue;
  return (
    <section aria-labelledby="res-h" className="tj-card px-4 pt-[18px] pb-2 md:p-6">
      <SectionHead eyebrow={cats.length === 1 ? cats[0] : "Эцэг эхэд"} title={<span id="res-h">Сургалт</span>}
        right={<MoreLink to="/learning"><span className="md:hidden">Бүгд</span><span className="hidden md:inline">Бүгдийг харах</span></MoreLink>} />
      <div className="md:hidden">{series.map((s, i) => <SeriesRow key={s.id} s={s} i={i} cont={cont} />)}</div>
      <div className="hidden md:grid gap-4 mt-3 grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
        {series.map((s, i) => <SeriesTile key={s.id} s={s} i={i} cont={cont} />)}
      </div>
    </section>
  );
}
